import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { getPostMeta } from '@/lib/postMeta';
import { getDisplayName } from '@/lib/getDisplayName';
import { useAuthor } from '@/hooks/useAuthor';
import { PostMedia } from '@/components/PostMedia';

interface BlogPostItemProps {
  event: NostrEvent;
}

export function BlogPostItem({ event }: BlogPostItemProps) {
  const meta = getPostMeta(event);
  const author = useAuthor(event.pubkey);
  const authorName = getDisplayName(author.data?.metadata, event.pubkey);
  const authorNpub = nip19.npubEncode(event.pubkey);
  const nevent = nip19.neventEncode({ id: event.id, author: event.pubkey });
  const postUrl = `/${nevent}`;

  return (
    <article className="group flex flex-col">
      {/* Featured media (looping video or image) */}
      {(meta.video || meta.image) && (
        <Link
          to={postUrl}
          className="block overflow-hidden rounded-xl bg-muted shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <PostMedia video={meta.video} image={meta.image} alt={meta.title} />
        </Link>
      )}

      <div className="mt-4">
        {/* Category tag */}
        {meta.tag && (
          <Link
            to={`/${nevent}`}
            className="inline-block text-xs font-semibold uppercase tracking-widest text-accent hover:underline"
          >
            {meta.tag}
          </Link>
        )}

        {/* Title */}
        <h2 className="mt-2 font-display text-2xl sm:text-[28px] leading-tight font-bold">
          <Link
            to={postUrl}
            className="transition-colors hover:text-primary focus-visible:outline-none focus-visible:underline"
          >
            {meta.title}
          </Link>
        </h2>

        {/* Excerpt */}
        {meta.excerpt && meta.excerpt !== meta.title && (
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground line-clamp-3">
            {meta.excerpt}
          </p>
        )}

        {/* Byline */}
        <p className="mt-4 text-sm text-muted-foreground">
          Posted on{' '}
          <span className="text-foreground/80">{meta.date}</span> by{' '}
          <Link
            to={`/${authorNpub}`}
            className="font-medium text-primary hover:underline"
          >
            {authorName}
          </Link>
        </p>
      </div>
    </article>
  );
}
